import { ScrollText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { type COISentLog, coiSentLogDownloadHref } from '../../../../models/coi'

function formatDateMMDDYYYY(dateStr: string): string {
  const date = new Date(dateStr)
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const yyyy = date.getFullYear()
  return `${mm}/${dd}/${yyyy}`
}

interface COISentLogTableProps {
  logs: COISentLog[]
  isLoading: boolean
  showHolder?: boolean
  emptyMessage?: string
  orgId?: string
}

export function COISentLogTable({
  logs,
  isLoading,
  showHolder = true,
  emptyMessage = 'No COI sent logs found.',
  orgId,
}: COISentLogTableProps) {
  if (isLoading) {
    return <SkeletonRows rows={3} />
  }

  if (logs.length === 0) {
    return <p className="text-muted-foreground text-sm">{emptyMessage}</p>
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-semibold">Date</TableHead>
          <TableHead className="font-semibold">Sent by</TableHead>
          {showHolder && <TableHead className="font-semibold">To</TableHead>}
          <TableHead className="font-semibold">View PDF</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {logs.map((log: COISentLog) => (
          <TableRow key={log.id}>
            <TableCell>{formatDateMMDDYYYY(log.dateSent)}</TableCell>
            <TableCell>{log.createdByName || 'Auto Renewal'}</TableCell>
            {showHolder && <TableCell>{log.certificateHolderEmail}</TableCell>}
            <TableCell>
              {log.hasDocument ? (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground hover:text-foreground"
                  href={coiSentLogDownloadHref(log.id, orgId)}
                  target="_blank"
                  aria-label="View COI PDF"
                >
                  <ScrollText className="size-4" />
                </Button>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </TableCell>
            <TableCell>
              {log.isLatestVersion && <span className="text-sm font-medium text-green-600">Latest COI</span>}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
